import React, { useState, useEffect } from 'react';
import { ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { RateLimitError } from '../types';
import { AUTH_MESSAGES, getRateLimitMessage } from '../constants/messages';

interface RateLimitFeedbackProps {
  rateLimitInfo: RateLimitError;
  onRetryAvailable?: () => void;
  className?: string;
}

const RateLimitFeedback: React.FC<RateLimitFeedbackProps> = ({
  rateLimitInfo,
  onRetryAvailable,
  className = ''
}) => {
  const [timeLeft, setTimeLeft] = useState(rateLimitInfo.retry_after);

  useEffect(() => {
    if (timeLeft <= 0) {
      onRetryAvailable?.();
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        const newTime = prev - 1;
        if (newTime <= 0) {
          clearInterval(timer);
          onRetryAvailable?.();
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, onRetryAvailable]);

  const formatTime = (seconds: number): string => {
    if (seconds <= 0) return '0s';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  const getProgressPercentage = (): number => {
    return ((rateLimitInfo.retry_after - timeLeft) / rateLimitInfo.retry_after) * 100;
  };

  return (
    <div className={`p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md ${className}`}>
      <div className="flex items-start space-x-3">
        <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h4 className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-1">
            Rate Limit Exceeded
          </h4>
          <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
            {getRateLimitMessage(timeLeft, 'countdown')}
          </p>
          
          {/* Progress Bar */}
          <div className="mb-3">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs text-amber-600 dark:text-amber-400">
                Time remaining
              </span>
              <span className="text-xs font-medium text-amber-800 dark:text-amber-200 flex items-center">
                <ClockIcon className="h-3 w-3 mr-1" />
                {formatTime(timeLeft)}
              </span>
            </div>
            <div className="w-full bg-amber-200 dark:bg-amber-800 rounded-full h-2">
              <div
                className="bg-amber-500 h-2 rounded-full transition-all duration-1000 ease-linear"
                style={{ width: `${getProgressPercentage()}%` }}
              />
            </div>
          </div>

          {/* Rate Limit Details */}
          <div className="text-xs text-amber-600 dark:text-amber-400 space-y-1">
            <div className="flex justify-between">
              <span>Requests limit:</span>
              <span className="font-medium">{rateLimitInfo.limit}</span>
            </div>
            <div className="flex justify-between">
              <span>Remaining requests:</span>
              <span className="font-medium">{rateLimitInfo.remaining}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RateLimitFeedback;